import { useEffect, useState } from "react";
import { MailWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/api.js";
import { getUser, isLoggedIn, setUser } from "@/auth.js";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/formatDateTime.js";

// Sticky amber banner rendered at the top of every AppLayout page while the
// signed-in user is a founder still inside their email-verification grace
// window (needs_verification === true, with a grace deadline). Everyone past
// grace / non-founder is hard-blocked by the API gate and never reaches the
// shell, so this banner is purely the founder nag.
//
// Source of truth is the server: on mount we refresh /auth/me (single-flighted
// in api.ts) and sync the flags into the stored user, so a page load after the
// user verified elsewhere clears the banner without a manual reload.

const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyEmailBanner() {
  const [needs, setNeeds] = useState<boolean>(
    () => getUser()?.needs_verification === true,
  );
  const [graceUntil, setGraceUntil] = useState<string | null>(
    () => getUser()?.verification_grace_until ?? null,
  );
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Authoritative refresh on mount.
  useEffect(() => {
    if (!isLoggedIn()) return;
    let cancelled = false;
    void api
      .me()
      .then((me) => {
        if (cancelled) return;
        setNeeds(me.user.needs_verification);
        setGraceUntil(me.user.verification_grace_until);
        const current = getUser();
        if (current) {
          setUser({
            ...current,
            needs_verification: me.user.needs_verification,
            verification_grace_until: me.user.verification_grace_until,
          });
        }
      })
      .catch(() => {
        /* keep the optimistic stored value */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  if (!needs) return null;

  async function resend() {
    if (sending || cooldown > 0) return;
    setSending(true);
    try {
      await api.resendVerification();
      toast.success("Verification email sent. Check your inbox.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      // 429 → the server cooldown bit before ours; surface it gently.
      if (e instanceof ApiError && e.status === 429) {
        toast.error("Please wait a moment before resending.");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        toast.error(
          e instanceof ApiError ? e.message : "Couldn't resend the email.",
        );
      }
    } finally {
      setSending(false);
    }
  }

  const deadline = graceUntil ? formatDate(graceUntil) : null;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm text-warning-text"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <MailWarning className="h-4 w-4 shrink-0" aria-hidden />
        <span className="font-semibold">Verify your email</span>
        <span>
          Check your inbox for the verification link
          {deadline ? (
            <>
              . Your workspace stays open until{" "}
              <strong className="font-semibold">{deadline}</strong>.
            </>
          ) : (
            "."
          )}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-warning/40 bg-transparent hover:bg-warning/20"
        onClick={resend}
        disabled={sending || cooldown > 0}
      >
        {sending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
      </Button>
    </div>
  );
}
